import { supabase } from "@/integrations/supabase/client";

interface StorageCheckResult {
  isAccessible: boolean;
  error?: string;
  bucketName?: string;
}

/**
 * Checks if a Supabase storage bucket is accessible
 */
export async function checkStorageAccessibility(
  bucketName: string
): Promise<StorageCheckResult> {
  try {
    console.log(`Checking accessibility of storage bucket: ${bucketName}`);

    // First check if we have a valid connection
    if (!supabase) {
      console.error("Supabase client not initialized");
      return {
        isAccessible: false,
        error:
          "Storage connection not available: Supabase client not initialized. This typically indicates a configuration issue.",
        bucketName,
      };
    }

    // Set a timeout for the request to prevent hanging
    const timeoutPromise = new Promise<StorageCheckResult>((_, reject) =>
      setTimeout(() => reject(new Error("Storage check timed out")), 5000)
    );

    // Try to list bucket contents as a faster check than getting bucket details
    const controller = new AbortController();
    const signal = controller.signal;

    const checkPromise = supabase.storage
      .from(bucketName)
      .list("", { limit: 1 })
      .then(({ data, error }) => {
        if (error) {
          // Supabase specific error details
          let errorMessage = `Supabase Storage Error: ${error.message}`;
          if ((error as any).status)
            errorMessage += ` (Status: ${(error as any).status})`;
          if (error.name) errorMessage += ` (Name: ${error.name})`;
          if ((error as any).code)
            errorMessage += ` (Code: ${(error as any).code})`; // Supabase errors might have a 'code'

          console.error(`Error listing bucket ${bucketName}:`, error);
          return {
            isAccessible: false,
            error: errorMessage,
            bucketName,
          };
        }
        console.log(`Successfully accessed bucket ${bucketName}`);
        return {
          isAccessible: true,
          bucketName,
        };
      })
      .catch((error) => {
        // Handle client-side/network errors
        let errorMessage = "An unexpected error occurred during storage check.";
        if (error instanceof Error) {
          if (error.name === "AbortError") {
            errorMessage =
              "Storage check cancelled: The request was aborted (e.g., due to timeout or navigation).";
          } else if (error.message.includes("Network Error")) {
            // Common for fetch API
            errorMessage =
              "Network Error: Could not connect to the storage service. Please check your internet connection.";
          } else {
            errorMessage = `Client-side Error: ${error.message}`;
          }
        } else if (
          typeof error === "object" &&
          error !== null &&
          "message" in error
        ) {
          // Attempt to get more specific error details if it's an object (e.g., from a network response)
          errorMessage = `API Error: ${error.message}`;
          if ("status" in error)
            errorMessage += ` (Status: ${(error as any).status})`;
          if ("code" in error)
            errorMessage += ` (Code: ${(error as any).code})`;
        } else {
          errorMessage = `Unknown Error Type: ${String(error)}`;
        }

        console.error(`Exception accessing bucket ${bucketName}:`, error);
        return {
          isAccessible: false,
          error: errorMessage,
          bucketName,
        };
      });

    // Race between timeout and actual check
    try {
      // Set a timeout to abort the request after 5 seconds
      setTimeout(() => controller.abort(), 5000);
      return await Promise.race([checkPromise, timeoutPromise]);
    } catch (timeoutError) {
      console.error("Storage check timed out:", timeoutError);
      controller.abort(); // Make sure to abort the request if we timeout
      return {
        isAccessible: false,
        error:
          "Storage check timed out: The operation took too long to complete. This could indicate a slow network or server issue.",
        bucketName,
      };
    }
  } catch (err) {
    console.error(
      `Unhandled critical exception in checkStorageAccessibility for bucket ${bucketName}:`,
      err
    );
    return {
      isAccessible: false,
      error: `Critical System Error: ${
        err instanceof Error ? err.message : String(err)
      }. Please contact support.`,
      bucketName,
    };
  }
}

/**
 * Checks storage health but don't display toast notifications
 */
export async function checkStorageHealth(
  bucketName = "portfolio-images"
): Promise<boolean> {
  console.log(`Checking storage health for bucket: ${bucketName}`);
  try {
    // Don't try to create the bucket - just check if it's accessible
    // This avoids potential permission issues
    const storageHealth = await checkStorageAccessibility(bucketName);

    if (!storageHealth.isAccessible) {
      console.error(
        `Storage accessibility check failed for ${bucketName}:`,
        storageHealth.error
      );
      return false;
    }

    return true;
  } catch (error) {
    console.error(`Error in checkStorageHealth for ${bucketName}:`, error);
    return false;
  }
}

/**
 * Get storage buckets information
 */
export async function getStorageBuckets(): Promise<
  { name: string; isAccessible: boolean }[]
> {
  try {
    const { data: buckets, error } = await supabase.storage.listBuckets();

    if (error) {
      console.error("Error listing storage buckets:", error);
      return [];
    }

    // Check accessibility of each bucket
    const bucketsWithStatus = await Promise.all(
      buckets.map(async (bucket) => {
        const { isAccessible } = await checkStorageAccessibility(bucket.name);
        return {
          name: bucket.name,
          isAccessible,
        };
      })
    );

    return bucketsWithStatus;
  } catch (error) {
    console.error("Error getting storage buckets info:", error);
    return [];
  }
}

/**
 * Attempts to repair or workaround common image loading issues
 */
export async function attemptStorageRepair(
  imageUrl: string,
  options: { forceCacheBusting?: boolean } = {}
): Promise<string> {
  if (!imageUrl) return imageUrl;

  try {
    // Don't process data URLs or invalid URLs
    if (
      imageUrl.startsWith("data:") ||
      imageUrl.startsWith("blob:") ||
      !imageUrl.startsWith("http")
    ) {
      return imageUrl;
    }

    // Only add cache-busting when explicitly requested (e.g., for refresh operations)
    if (options.forceCacheBusting) {
      const url = new URL(imageUrl);
      const cacheBuster = `_cb=${Date.now()}`;
      const separator = url.search ? "&" : "?";
      return `${imageUrl}${separator}${cacheBuster}`;
    }

    // For normal processing, just return the URL as-is to maintain stability
    return imageUrl;
  } catch (error) {
    console.error("Error in attemptStorageRepair:", error);
    return imageUrl;
  }
}

// Extract bucket and path from a URL
const extractBucketAndPath = (
  url: string
): { bucket: string; path: string; separator: string } => {
  try {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split("/storage/v1/object/public/");
    if (pathParts.length > 1) {
      const [bucketAndPath] = pathParts[1].split("?");
      const [bucket, ...pathParts2] = bucketAndPath.split("/");
      const path = pathParts2.join("/");
      return {
        bucket,
        path,
        separator: urlObj.search ? "&" : "?",
      };
    }
  } catch (error) {
    console.error("Error extracting bucket and path:", error);
  }

  return {
    bucket: "",
    path: "",
    separator: "",
  };
};

/**
 * Diagnose storage issues and check policy configuration
 */
export async function diagnoseStorageIssues(bucketName: string): Promise<{
  accessible: boolean;
  hasPolicies: boolean;
  details: string;
}> {
  try {
    // First check if bucket is accessible
    const accessCheck = await checkStorageAccessibility(bucketName);

    // Try to get a general sense of policies
    const { data: policies, error: policiesError } = await supabase.rpc(
      "get_policies_for_bucket",
      { bucket_name: bucketName }
    );

    const hasPolicies =
      policies && Array.isArray(policies) && policies.length > 0;

    return {
      accessible: accessCheck.isAccessible,
      hasPolicies,
      details: accessCheck.isAccessible
        ? "Storage is accessible"
        : `Storage issue: ${accessCheck.error || "Unknown error"}`,
    };
  } catch (error) {
    console.error("Error diagnosing storage issues:", error);
    return {
      accessible: false,
      hasPolicies: false,
      details: `Error during diagnosis: ${
        error instanceof Error ? error.message : String(error)
      }`,
    };
  }
}
